import {useEffect, useState} from "react";
import {Button, Checkbox, Group, Modal, Stack, Text, TextInput} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {dibApi} from "../../../chat/dibApi";
import {buildCollectionUrl, type TopicMeta} from "./utils";

type DuplicateFillingModalProps = {
    opened: boolean;
    onClose: () => void;
    collectionId: string;
    collectionName: string;
    topicMeta: TopicMeta;
    hasOnlyInfoNodes: boolean;
};

const initialOptions = {
    comment: true,
    na: true,
    ok: false,
    deviation: false,
    variables: true,
};

export const DuplicateFillingModal = ({
    opened,
    onClose,
    collectionId,
    collectionName,
    topicMeta,
    hasOnlyInfoNodes,
}: DuplicateFillingModalProps) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [duplicateCollection, {data, isLoading}] =
        dibApi.endpoints.duplicateCollection.useMutation();

    const [title, setTitle] = useState(t("Phrase.DuplicateOfTitle", {fillingTitle: collectionName}));
    const [options, setOptions] = useState(initialOptions);

    const toggle = (key: keyof typeof initialOptions) =>
        setOptions((prev) => ({...prev, [key]: !prev[key]}));

    const isValid =
        title.length > 0 &&
        (hasOnlyInfoNodes ||
            options.comment ||
            options.na ||
            options.ok ||
            options.deviation ||
            options.variables);

    const handleDuplicate = () => {
        if (!title) return;
        duplicateCollection({
            collectionId,
            topicId: topicMeta.topicId,
            name: title,
            ...options,
        });
    };

    useEffect(() => {
        if (data) {
            navigate(
                buildCollectionUrl(topicMeta, {
                    name: data.name,
                    id: data.collectionId.toLowerCase(),
                }),
            );
            onClose();
        }
    }, [data, navigate, onClose, topicMeta]);

    return (
        <Modal opened={opened} onClose={onClose} title={t("Action.DuplicateFilling")}>
            <Stack>
                <TextInput
                    label={t("Label.DuplicateName")}
                    value={title}
                    onChange={(e) => setTitle(e.currentTarget.value)}
                />

                {!hasOnlyInfoNodes && (
                    <>
                        <Text fw={600} size="sm">
                            {t("Info.DataToDuplicate")}
                        </Text>
                        <Checkbox
                            label={t("Word.Comments")}
                            checked={options.comment}
                            onChange={() => toggle("comment")}
                        />
                        <Checkbox
                            label={`${t("Word.Status")}: ${t("Status.NA")}`}
                            checked={options.na}
                            onChange={() => toggle("na")}
                        />
                        <Checkbox
                            label={`${t("Word.Status")}: ${t("Status.Ok")}`}
                            checked={options.ok}
                            onChange={() => toggle("ok")}
                        />
                        <Checkbox
                            label={`${t("Word.Status")}: ${t("Status.Deviation")}`}
                            checked={options.deviation}
                            onChange={() => toggle("deviation")}
                        />
                        <Checkbox
                            label={t("Phrase.FieldsInDocumentGenerator")}
                            checked={options.variables}
                            onChange={() => toggle("variables")}
                        />
                    </>
                )}
            </Stack>

            <Group justify="flex-end" mt="lg">
                <Button variant="subtle" onClick={onClose}>
                    {t("common.cancel")}
                </Button>
                <Button
                    disabled={!isValid}
                    loading={isLoading}
                    onClick={handleDuplicate}
                    title={
                        isValid
                            ? t("Action.DuplicateFilling")
                            : t("Warn.DataToDuplicateNotChosen")
                    }
                >
                    {t("Word.Duplicate")}
                </Button>
            </Group>
        </Modal>
    );
};
